import { ref, defineComponent, watch } from 'vue';
import useConfigInject from '../../_util/hooks/useConfigInject';
import classNames from '../../_util/classNames';
import { getTextWidth } from 'wetrade-tools';
export const XAxisProps = () => ({
    left: {
        type: [String, Number],
        default: '28px',
    },
    right: {
        type: [String, Number],
        default: '4px',
    },
    xLabelList: {
        type: Array,
        default: () => [],
    },
    // 对齐方式，shadow 居中对齐，line 两端对齐
    isShowTooltipAxis: {
        type: String,
        default: 'shadow',
    },
    // 是否显示全部x轴label
    showCustomXAll: {
        type: Boolean,
        default: false,
    },
    // X轴文字是否溢出隐藏
    xLabelOverflow: {
        type: Boolean,
        default: false,
    },
});
const XAxis = defineComponent({
    name: 'WdXAxis',
    props: XAxisProps(),
    setup(props, {}) {
        const { prefixCls } = useConfigInject('xAxis', props);
        const mainRef = ref();
        const labelList = ref([]);
        // 更新X轴
        const updateXLabel = (xLabel) => {
            labelList.value = [];
            const labelLen = xLabel.length;
            for (let i = 0; i < labelLen; i++) {
                // label少于4个 全部显示
                // 是第一个，或最后一个，或处于中间值，显示
                const isFirst = !i, isEnd = i === labelLen - 1;
                let isShow = false;
                if (typeof xLabel[i] === 'object') {
                    isShow = !!xLabel[i]?.isShow;
                }
                else {
                    isShow =
                        props.showCustomXAll ||
                            isFirst ||
                            isEnd ||
                            i === Math.ceil((labelLen - 1) / 2) ||
                            labelLen <= 4;
                }
                let text = xLabel[i];
                if (typeof xLabel[i] === 'object') {
                    text = xLabel[i]?.text || text;
                }
                labelList.value.push({
                    isShow,
                    text,
                });
                // 如果指示器类型是shadow 阴影，每个模块所占比例都一致，居中对齐
                labelList.value[i].textAlign =
                    props.isShowTooltipAxis === 'shadow'
                        ? 'center'
                        : getTextAlign(isFirst, labelLen, isEnd).textAlign;
                labelList.value[i].flex =
                    props.isShowTooltipAxis === 'shadow' ? 1 : isFirst || isEnd ? 1 : 2;
            }
        };
        // 对齐方式
        function getTextAlign(isFirst, labelLen, isEnd) {
            const textAlignMap = [
                [
                    () => isFirst,
                    () => {
                        return { textAlign: labelLen === 1 ? 'center' : 'left' };
                    }, // 执行函数
                ],
                [
                    () => isEnd,
                    () => {
                        return { textAlign: 'right' };
                    },
                ],
                [
                    () => !isFirst && !isEnd,
                    () => {
                        return { textAlign: 'center' };
                    },
                ],
            ];
            // 获取符合条件的子数组
            const getDescribe = textAlignMap.find(item => item[0]());
            // 子数组存在则运行子数组中的第二个元素（执行函数）
            return getDescribe ? getDescribe[1]() : '';
        }
        watch(() => props.xLabelList, val => updateXLabel(val), {
            immediate: true,
        });
        return () => {
            const list = labelList.value || [];
            const wrapperStyle = {
                paddingLeft: typeof props.left === 'string' ? props.left : props.left + 'px',
                paddingRight: typeof props.right === 'string' ? props.right : props.right + 'px',
            };
            const itemClassName = classNames({
                [prefixCls.value + '__item']: true,
                [prefixCls.value + '__ellipsis']: props.xLabelOverflow,
            });
            const paddingLeft = parseFloat(wrapperStyle.paddingLeft);
            const paddingRight = parseFloat(wrapperStyle.paddingRight);
            const wrapperWidth = mainRef.value?.offsetWidth - paddingLeft - paddingRight;
            const unitPixel = props.isShowTooltipAxis === 'shadow'
                ? wrapperWidth / list.length
                : wrapperWidth / (list.length - 1);
            const unitPixelPercent = (unitPixel / wrapperWidth) * 100;
            let renderWidth = 0;
            // 处理渲染列表
            const renderList = list.reduce((prev, cur, index) => {
                if (cur.isShow) {
                    const textWidth = getTextWidth({ text: cur.text });
                    renderWidth += Math.max(unitPixel, textWidth);
                    let translateX = '0px';
                    if (props.isShowTooltipAxis !== 'shadow') {
                        if (cur.textAlign === 'center') {
                            translateX = '-50%';
                        }
                        else if (cur.textAlign === 'right') {
                            translateX = '-100%';
                        }
                    }
                    const textPercent = Math.max(unitPixelPercent, (textWidth / wrapperWidth) * 100);
                    const totalPercent = prev.length ? prev[prev.length - 1].totalPercent : 0;
                    const item = {
                        ...cur,
                        index,
                        translateX,
                        textPercent,
                        textWidth,
                        left: unitPixelPercent * index,
                        totalPercent: totalPercent + textPercent,
                        unitPixel,
                    };
                    return [...prev, item];
                }
                return prev;
            }, []);
            const otherLen = list.length - renderList.length;
            if (props.isShowTooltipAxis === 'shadow' && otherLen > 0) {
                const otherWrapperWidth = wrapperWidth - renderWidth;
                const otherUnitPixel = otherWrapperWidth / (list.length - renderList.length);
                const otherUnitPixelPercent = (otherUnitPixel / wrapperWidth) * 100; // 这个需要重新计算
                renderList.forEach((item, index) => {
                    const otherPixelPercent = (item.index - index) * otherUnitPixelPercent;
                    item.left = item.totalPercent - item.textPercent + otherPixelPercent;
                });
            }
            const labelWidth = (wrapperWidth / renderList.length / wrapperWidth) * 100;
            const className = classNames({
                [prefixCls.value]: true,
                [`${prefixCls.value}__flex`]: otherLen === 0,
            });
            let vNode;
            if (otherLen === 0) {
                // 显示全部的时候用flex布局
                vNode = (<>
            {list.map((arr, index) => {
                        const style = {
                            textAlign: arr.textAlign,
                            overflow: arr.isShow ? 'visible' : 'hidden',
                            flex: arr.flex,
                        };
                        const textStyle = {
                            display: arr.isShow ? 'block' : 'none',
                        };
                        return (<span class={itemClassName} key={index} style={style}>
                  <span style={textStyle}> {arr.text}</span>
                </span>);
                    })}
          </>);
            }
            else {
                vNode = (<div class={`${prefixCls.value}__box`}>
            {renderList.map(arr => {
                        let w;
                        if (props.isShowTooltipAxis !== 'shadow') {
                            w = labelWidth;
                        }
                        else {
                            w = arr.textPercent;
                        }
                        // 宽度和平移必须用百分比，否则在图表宽度变化时x轴label定位不会发生变化
                        const style = {
                            width: w + '%',
                            textAlign: arr.textAlign,
                            left: arr.left + '%',
                            transform: `translateX(${arr.translateX})`,
                        };
                        return (<span class={itemClassName} key={arr.index} style={style}>
                  <span> {arr.text}</span>
                </span>);
                    })}
          </div>);
            }
            return (<div class={className} ref={mainRef} style={wrapperStyle}>
          {vNode}
        </div>);
        };
    },
});
export default XAxis;
